import type {
  OperatorTrustSessionState,
  RunTimelineEntry,
  SessionHealthView,
  TurnPhaseView,
} from "./state.ts";

type TrustHost = {
  sessionKey: string;
  operatorTrustBySession: Record<string, OperatorTrustSessionState>;
  operatorTrustLastSignalAt?: number;
};

function touchSignal(host: TrustHost): void {
  host.operatorTrustLastSignalAt = Date.now();
}

function patchSession(
  host: TrustHost,
  sessionKey: string,
  patch: Partial<OperatorTrustSessionState>,
): void {
  const key = sessionKey.trim();
  if (!key) {
    return;
  }
  const prev = host.operatorTrustBySession[key] ?? { timeline: [] };
  host.operatorTrustBySession = {
    ...host.operatorTrustBySession,
    [key]: { ...prev, ...patch },
  };
  touchSignal(host);
}

function appendTimeline(host: TrustHost, sessionKey: string, entry: RunTimelineEntry): void {
  const key = sessionKey.trim();
  if (!key) {
    return;
  }
  const prev = host.operatorTrustBySession[key] ?? { timeline: [] };
  const next = [entry, ...prev.timeline.filter((row) => row.id !== entry.id)].slice(0, 48);
  patchSession(host, key, { timeline: next });
}

export function handleSessionHealthEvent(
  host: TrustHost,
  payload: { sessionKey?: string; health?: string; reason?: string | null; atMs?: number },
): void {
  const sessionKey = payload.sessionKey?.trim();
  const health = payload.health;
  if (!sessionKey || (health !== "healthy" && health !== "suspect" && health !== "wedged")) {
    return;
  }
  const view: SessionHealthView = {
    health,
    reason: payload.reason ?? null,
    atMs: payload.atMs ?? Date.now(),
  };
  patchSession(host, sessionKey, { sessionHealth: view });
  if (health !== "healthy") {
    appendTimeline(host, sessionKey, {
      id: `health:${health}:${view.atMs}`,
      kind: "health",
      summary: health === "wedged" ? "Session wedged" : "Session slow",
      detail: view.reason ?? undefined,
      atMs: view.atMs,
    });
  }
}

export function handleChatTurnEvent(
  host: TrustHost,
  payload: {
    sessionKey?: string;
    clientRunId?: string;
    from?: string;
    to?: string;
    atMs?: number;
  },
): void {
  const sessionKey = payload.sessionKey?.trim();
  const to = payload.to?.trim();
  if (!sessionKey || !to) {
    return;
  }
  const view: TurnPhaseView = {
    clientRunId: payload.clientRunId?.trim() ?? "",
    from: payload.from ?? "none",
    to,
    atMs: payload.atMs ?? Date.now(),
  };
  patchSession(host, sessionKey, { turnPhase: view });
}

export function handleOperatorTimelineEvent(
  host: TrustHost,
  payload: {
    sessionKey?: string;
    kind?: string;
    summary?: string;
    detail?: string | null;
    atMs?: number;
  },
): void {
  const sessionKey = payload.sessionKey?.trim();
  const summary = payload.summary?.trim();
  if (!sessionKey || !summary) {
    return;
  }
  const atMs = payload.atMs ?? Date.now();
  appendTimeline(host, sessionKey, {
    id: `timeline:${payload.kind ?? "event"}:${atMs}:${summary.slice(0, 24)}`,
    kind: payload.kind ?? "event",
    summary,
    detail: payload.detail ?? undefined,
    atMs,
  });
}
